
import Vue from 'vue';
import { Alert, AlertEdit } from '@/interfaces/alerts';
import AlertList from '@/components/alerts/AlertList.vue';
import { Device } from '@/interfaces/locations';
import ToolTip from '@/components/common/ToolTip.vue';
import permissions from '@/lib/permissions';
import { AxiosError } from 'axios';
import dayjs from '@/plugins/dayjs';
import { Site } from '@/interfaces/multiTenancy';
import { Controller } from '@/interfaces/controllers';

interface ComponentData {
	showAlertHistory: boolean;
	currentMonth: {
		start: dayjs.Dayjs;
		end: dayjs.Dayjs;
	};
	loadingAlertHistory: boolean;
}

export default Vue.extend({
	components: {
		AlertList,
		ToolTip,
	},
	data(): ComponentData {
		return {
			showAlertHistory: false,
			currentMonth: {
				start: dayjs().utc(true).startOf('month'),
				end: dayjs().utc(true).endOf('month'),
			},
			loadingAlertHistory: false,
		};
	},
	computed: {
		selectedSite(): Site | null {
			return this.$store.getters['tenancy/sites/getSelectedSite'];
		},

		currentMonthValue(): string {
			return this.currentMonth.start.format('YYYY-MM');
		},

		alerts(): Alert[] {
			if (this.showAlertHistory) {
				return this.$store.getters['activities/alerts/getCustomAlerts'];
			} else {
				return this.$store.getters['activities/alerts/getActiveAlerts'];
			}
		},

		userCanRead(): boolean {
			return permissions.hasPermission(this.$store.getters['usermanagement/user/getRole'], [
				'alerts',
				'read',
			]);
		},

		userCanWrite(): boolean {
			return permissions.hasPermission(this.$store.getters['usermanagement/user/getRole'], [
				'alerts',
				'write',
			]);
		},

		devices(): Device[] {
			return [
				...this.$store.getters['devices/luminaires/getLuminaires'],
				...this.$store.getters['devices/switches/getSwitches'],
			];
		},

		controllers(): Controller[] {
			return this.$store.getters['controllers/controllers/getControllers'];
		},
	},
	watch: {
		async selectedSite(newSite) {
			if (this.showAlertHistory && newSite) {
				await this.fetchMonthAlerts();
			}
		},
	},
	methods: {
		async fetchMonthAlerts(): Promise<void> {
			await this.$store.dispatch('activities/alerts/fetchCustomAlerts', {
				params: {
					active: false,
					start_date: this.currentMonth.start.toISOString(),
					end_date: this.currentMonth.end.toISOString(),
				},
			});
		},

		async changeMonth(month: string): Promise<void> {
			this.currentMonth = {
				start: dayjs(month).utc(true).startOf('month'),
				end: dayjs(month).utc(true).endOf('month'),
			};
			this.loadingAlertHistory = true;
			try {
				await this.fetchMonthAlerts();
			} catch (error) {
				this.$store.dispatch('tools/messageHandling/addErrorMessage', {
					id: 'error-fetch-alerts',
					message: (error as AxiosError<{ message: string }>).response!.data.message,
				});
			}
			this.loadingAlertHistory = false;
		},

		async changeAlertType(): Promise<void> {
			if (this.showAlertHistory) {
				this.showAlertHistory = false;
			} else {
				this.loadingAlertHistory = true;
				try {
					this.showAlertHistory = true;
					await this.fetchMonthAlerts();
				} catch (error) {
					this.$store.dispatch('tools/messageHandling/addErrorMessage', {
						id: 'error-fetch-alerts',
						message: (error as AxiosError<{ message: string }>).response!.data.message,
					});
				}
				this.loadingAlertHistory = false;
			}
		},

		async changeAlertAck(id: number, editedAlert: AlertEdit): Promise<void> {
			this.$store.dispatch('tools/loading/changeLoadingStatus');
			try {
				await this.$store.dispatch('activities/alerts/modifyAlert', { id: id, alert: editedAlert });
			} catch (error) {
				this.$store.dispatch('tools/messageHandling/addErrorMessage', {
					id: 'error-modify-alerts',
					message: (error as AxiosError<{ message: string }>).response!.data.message,
				});
			}
			this.$store.dispatch('tools/loading/changeLoadingStatus');
		},
	},
});
